import { BehaviorSubject, Subscription } from 'rxjs';
import { ClientPayload } from '../../interfaces/client-payload.interface';
import { ConfigServiceProvider } from '../../../core/services/config-service/config-service';
import { FilterServiceProvider, FilterType } from '../../../core/services/filter-service/FilterServiceProvider';
import { HttpClient } from '@angular/common/http';
import { IClientModel } from '../../interfaces/i-client-model.interface';
import { IClientResults } from '../../interfaces/i-clients-results.interface';
import { IClientStrategy } from '../../model/client.model';
import { IContactPerson } from '../../interfaces/i-contact-person.interface';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { StringUtils } from '../../../../utils/string-utils';

@Injectable()
export class ClientServiceProvider {
  private clients: BehaviorSubject<IClientModel[]>;
  private allClients: IClientModel[] = [];
  private latestFilter;

  constructor(private http: HttpClient, private configService: ConfigServiceProvider, private filterService: FilterServiceProvider) {
    this.clients = new BehaviorSubject([]);
    this.filterService.subscribe(FilterType.CLIENTS, (filter) => {
      this.latestFilter = filter;
      this.filterClientsArray(filter);
    });
  }

  public filterClientsArray(filter: any) {
    let filtered: IClientModel[] = this.allClients;

    if (!StringUtils.isEmpty(filter.sapId)) {
      filtered = filtered.filter((it) => it.params.sapId === Number.parseInt(filter.sapId));
    }
    if (!StringUtils.isEmpty(filter.lastEventFrom)) {
      filtered = filtered.filter((it) =>
        StringUtils.isEmpty(it.lastEventDate) ? false : new Date(it.lastEventDate).getTime() > new Date(filter.lastEventFrom).getTime(),
      );
    }
    if (!StringUtils.isEmpty(filter.lastEventTo)) {
      filtered = filtered.filter((it) =>
        StringUtils.isEmpty(it.lastEventDate) ? false : new Date(it.lastEventDate).getTime() < new Date(filter.lastEventTo).getTime(),
      );
    }
    if (!StringUtils.isEmpty(filter.lastEventStatus)) {
      filtered = filtered.filter((it) => it.lastEventStatus === filter.lastEventStatus);
    }
    if (!StringUtils.isEmpty(filter.lastEventType)) {
      filtered = filtered.filter((it) => it.lastEventType === filter.lastEventType);
    }
    if (!StringUtils.isEmpty(filter.segment)) {
      filtered = filtered.filter((it) => it.segment === filter.segment);
    }
    if (!StringUtils.isEmpty(filter.searchQuery)) {
      let searchQuery = filter.searchQuery.toLowerCase();
      filtered = filtered.filter((it) => this.checkAgainstQuery(it, searchQuery));
    }

    const sorted = this.sortArrayByObjectProperty(filtered, filter.sort, filter.order);

    this.clients.next(sorted);
  }

  private checkAgainstQuery(client: IClientModel, searchQuery): boolean {
    return JSON.stringify(client).toLowerCase().indexOf(searchQuery.toLowerCase()) > -1;
  }

  private sortArrayByObjectProperty(array: IClientModel[], sortedProperty: string, order?: string): any[] {
    array = array.sort((a, b) => this.compareClientsBySapId(a, b));

    if (sortedProperty) {
      let sorted = array.sort((objA, objB) => {
        const prop1 = objA[sortedProperty] ? objA[sortedProperty] : '9Zz';
        const prop2 = objB[sortedProperty] ? objB[sortedProperty] : '9Zz';
        if ('number' === typeof prop1 && 'number' === typeof prop2) {
          return prop1 > prop2 ? 1 : prop2 > prop1 ? -1 : 0;
        }
        return String(prop1).localeCompare(String(prop2), 'pl', {
          sensitivity: 'base',
          ignorePunctuation: true,
        });
      });

      if ('DESC' === order) return sorted.reverse();
      else return sorted;
    }
    return array;
  }

  private compareClientsBySapId(firstClient: IClientModel, secondClient: IClientModel): number {
    if (firstClient.params.sapId && secondClient.params.sapId) {
      return firstClient.params.sapId - secondClient.params.sapId;
    }

    // Move clients without sapId to the end of array
    if (firstClient.params.sapId && !secondClient.params.sapId) {
      return -1;
    }
    if (!firstClient.params.sapId && secondClient.params.sapId) {
      return 1;
    }

    // Compare clients without sapId using names
    return this.compareClientsByName(firstClient.name, secondClient.name);
  }

  private compareClientsByName(firstClientName: string, secondClientName: string): number {
    firstClientName = firstClientName.toLocaleLowerCase();
    secondClientName = secondClientName.toLocaleLowerCase();

    if (firstClientName < secondClientName) {
      return -1;
    }
    if (firstClientName > secondClientName) {
      return 1;
    }
    return 0;
  }

  public async load() {
    let url = this.configService.getUrl('clientsAsArray');
    try {
      const response = (await this.http.get(url).toPromise()) as IClientModel[];
      this.allClients = response;
      this.filterClientsArray(this.latestFilter);
    } catch (err) {
      console.error(err);
    }
  }

  public getAll(searchQuery: string): Promise<IClientModel[]> {
    let url = this.configService.getUrl('clientsAsArray');
    return this.http.get<IClientModel[]>(url, { params: { searchQuery: searchQuery } }).toPromise();
  }

  public createNewClient(client: IClientModel): Observable<any> {
    return this.http.post(this.configService.getUrl('clientForm'), client);
  }

  public updateClient(client: ClientPayload): Observable<any> {
    return this.http.put(this.configService.getUrl('clientForm'), client);
  }

  public createNewContact(contact: IContactPerson): Observable<any> {
    return this.http.post(this.configService.getUrl('contactForm'), contact);
  }

  public updateContact(contact: IContactPerson): Observable<any> {
    return this.http.put(this.configService.getUrl('updateContact').replace('{contactPersonId}', contact.id.toString()), contact);
  }

  public deleteContact(contactId: number): Observable<any> {
    return this.http.delete(this.configService.getUrl('deleteContact').replace('{contactPersonId}', contactId.toString()));
  }

  public getUserClientsAsArray(): Array<IClientModel> {
    return this.clients.getValue();
  }

  public subscribe(callback): Subscription {
    return this.clients.subscribe(callback);
  }

  public getById(id: number): IClientModel {
    return this.allClients.find((v) => v.clientId === id);
  }

  public getAllClients(): IClientModel[] {
    return this.allClients;
  }

  public getClientStrategy(clientId: number): Observable<IClientStrategy> {
    return this.http.get<IClientStrategy>(this.configService.getUrl('clientStrategy').replace('{id}', clientId.toString()));
  }

  public editClientStrategy(strategy: IClientStrategy): Observable<IClientStrategy> {
    return this.http.put<IClientStrategy>(
      this.configService.getUrl('clientStrategyEdit').replace('{id}', strategy.clientStrategyId.toString()),
      strategy,
    );
  }

  public getClientResults(clientId: number): Observable<Array<IClientResults>> {
    return this.http.get<Array<IClientResults>>(this.configService.getUrl('clientResults').replace('{id}', clientId.toString()));
  }

  public checkAgainstClient(client, searchQuery: string): boolean {
    return (
      StringUtils.checkIfStringsMatch(client.name || '', searchQuery, /[-_\s.,\'\"\`]/g) ||
      (client.params.nip && client.params.nip.toString().indexOf(searchQuery) > -1) ||
      (client.params.sapId && client.params.sapId.toString().indexOf(searchQuery) > -1)
    );
  }
}



// WEBPACK FOOTER //
// ./src/modules/clients/services/client-service/client-service.ts